import React, { FC } from 'react';
import { Table, TableBody, TableRow, TableCell, Skeleton, Box } from '@mui/material';

export type SkeletonColumnConfig = {
    type: 'text' | 'circular' | 'rectangular' | 'avatar-text' | 'button';
    width?: number | string;
    height?: number;
};

type SkeletonTableProps = {
    columns: SkeletonColumnConfig[];
    rows?: number;
    renderRowsOnly?: boolean;
};

const SkeletonTable: FC<SkeletonTableProps> = ({ columns, rows = 3, renderRowsOnly = false }) => {
    const renderSkeletonCell = (config: SkeletonColumnConfig, index: number) => {
        switch (config.type) {
            case 'avatar-text':
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="text" width={config.width || 120} />
                    </Box>
                );
            case 'circular':
                return <Skeleton variant="circular" width={config.width || 32} height={config.height || 32} />;
            case 'rectangular':
                return <Skeleton variant="rectangular" width={config.width || 80} height={config.height || 32} />;
            case 'button':
                return (
                    <Skeleton
                        variant="rectangular"
                        width={config.width || 80}
                        height={config.height || 32}
                        sx={{ borderRadius: 1 }}
                    />
                );
            case 'text':
            default:
                return <Skeleton variant="text" width={config.width || 100} height={config.height} />;
        }
    };

    const skeletonRows = Array.from({ length: rows }).map((_, rowIndex) => (
        <TableRow key={`skeleton-row-${rowIndex}`}>
            {columns.map((column, colIndex) => (
                <TableCell key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                    {renderSkeletonCell(column, colIndex)}
                </TableCell>
            ))}
        </TableRow>
    ));

    if (renderRowsOnly) {
        return <>{skeletonRows}</>;
    }

    return (
        <Table sx={{ width: '100%' }}>
            <TableBody>{skeletonRows}</TableBody>
        </Table>
    );
};

export default SkeletonTable;
